import { MousePointer2 } from 'lucide-react'
import { useStudioStore } from '@/store/studio-store'
import {
  SELECTION_BEARING_RAIL_ITEMS,
  useStudioSelectionContext,
} from '../selection-context'
import {
  useStudioConversationSurfaceLabel,
  useStudioProjectOutputKind,
} from './artifact-mode-switcher'
import { useStudioLocale } from '@/i18n'
import { resolveStudioOperationLabel } from '@/features/chat/services/studio-operation-display-copy'
import { COOGO_IDE_CHROME_COPY } from '../coogo-ide-chrome-copy'
import { presentStudioContextName } from '../studio-context-label'

interface ConversationTargetSummaryProps {
  onChangeTarget?: () => void
}

function safeSelectionLabel(
  selection: ReturnType<typeof useStudioSelectionContext>,
  currentPage: string,
  currentItem: string,
): string | null {
  if (!selection) return null
  if (selection.kind === 'page') {
    return selection.pageName && selection.pageName !== selection.pageId
      ? selection.pageName
      : currentPage
  }
  if (selection.kind === 'element') {
    return (
      selection.elementType ??
      presentStudioContextName(selection.label, currentItem)
    )
  }
  if (
    selection.label === selection.elementId ||
    selection.label === selection.blockId
  ) {
    return currentItem
  }
  return selection.label
    ? presentStudioContextName(selection.label, currentItem)
    : currentItem
}

function safeSelectionDetail(
  selection: ReturnType<typeof useStudioSelectionContext>,
): string | undefined {
  if (!selection?.detail) return undefined
  const rawIds = [
    selection.elementId,
    selection.blockId,
    selection.pageId,
    selection.blockName,
  ].filter((value): value is string => Boolean(value))
  return rawIds.some(
    (id) => selection.detail === id || selection.detail.includes(id),
  )
    ? undefined
    : selection.detail
}

export function ConversationTargetSummary({
  onChangeTarget,
}: ConversationTargetSummaryProps) {
  const { locale } = useStudioLocale()
  const copy = COOGO_IDE_CHROME_COPY[locale]
  const activeRailItem = useStudioStore((s) => s.activeRailItem)
  const context = useStudioStore((s) => s.chatContext)
  const selection = useStudioSelectionContext()
  const workspaceSurfaceLabel = useStudioConversationSurfaceLabel(activeRailItem)
  const outputKind = useStudioProjectOutputKind()
  const selectionApplies =
    !(activeRailItem === 'preview' && outputKind === 'document') &&
    SELECTION_BEARING_RAIL_ITEMS.has(activeRailItem)
  const surfaceTarget =
    activeRailItem === 'behavior'
      ? context.flowName && context.flowName !== context.flowId
        ? presentStudioContextName(context.flowName, copy.currentItem)
        : context.flowId
          ? copy.currentItem
          : undefined
      : activeRailItem === 'data'
        ? context.dataSourceName
        : undefined
  const selectionTarget = selectionApplies
    ? safeSelectionLabel(selection, copy.currentPage, copy.currentItem)
    : undefined
  const namedPage =
    context.pageName && context.pageName !== context.pageId
      ? context.pageName
      : null
  // `context.operationLabel` is the ENGLISH value `gateway-chat-service.ts`
  // sends to the model. What the operator reads is resolved from the operation
  // id instead, falling back to that English label when the id is not a
  // registry operation.
  const namedOperation =
    context.operationLabel && context.operationLabel !== context.operationId
      ? resolveStudioOperationLabel(
          context.operationId,
          locale,
          context.operationLabel,
        )
      : null
  const requestsReviewTarget =
    context.surfaceId === 'requests-review' && namedOperation
      ? namedOperation.split(' · ')
      : null
  const surfaceLabel = requestsReviewTarget?.[0] ?? workspaceSurfaceLabel
  const idOnlyTarget = context.pageId
    ? copy.currentPage
    : context.sourceWorkspaceId ||
        context.documentPageId ||
        context.documentId ||
        context.reportSectionId ||
        context.reportId
      ? copy.currentItem
      : null
  const selectedElementLabel = context.selectedElementLabel
    ? presentStudioContextName(context.selectedElementLabel, copy.currentItem)
    : null
  const selectedBlockLabel = context.selectedBlockLabel
    ? presentStudioContextName(context.selectedBlockLabel, copy.currentItem)
    : null
  const targetLabel =
    (requestsReviewTarget ? null : surfaceTarget) ||
    selectedElementLabel ||
    selectedBlockLabel ||
    selectionTarget ||
    namedPage ||
    namedOperation ||
    idOnlyTarget ||
    null
  const targetDetail =
    (requestsReviewTarget?.slice(1).join(' · ') || undefined) ??
    (selectionApplies ? safeSelectionDetail(selection) : undefined) ??
    context.documentTarget ??
    namedOperation ??
    copy.currentWorkspace

  return (
    <section
      className="shrink-0 border-b border-border bg-background px-3 py-3"
      data-testid="conversation-target-summary"
      aria-label={copy.selectedTarget}
    >
      <div className="mb-2 text-[11px] font-medium text-muted-foreground">
        {copy.selectedTarget}
      </div>
      <div className="flex min-w-0 items-center gap-2 rounded-md border border-border bg-card px-3 py-2.5">
        <MousePointer2 className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
        <div className="min-w-0 flex-1">
          <div className="truncate text-xs font-semibold text-foreground">
            {surfaceLabel}
            {targetLabel ? ` · ${targetLabel}` : ''}
          </div>
          <div className="mt-0.5 truncate text-[11px] text-muted-foreground">
            {targetDetail}
          </div>
        </div>
        {onChangeTarget ? (
          <button
            type="button"
            onClick={onChangeTarget}
            className="h-7 shrink-0 rounded-md border border-border bg-background px-2 text-[11px] font-medium text-foreground hover:bg-muted"
          >
            {copy.changeTarget}
          </button>
        ) : null}
      </div>
    </section>
  )
}
